import { FC, Fragment, MouseEvent, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, Stack, Tooltip } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { EditOutlined, VerifiedUserOutlined } from "@mui/icons-material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import { useToggle } from "rooks";
import CopyToClipboard from "react-copy-to-clipboard";
import LinkIcon from "@mui/icons-material/Link";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { useAppAbility } from "@/services/permissions";
import { useAGGridRowHoverHook } from "@/components/pages/common/ag-grid/utils/hooks/useAGGridRowHover.hook";
import { PartialDashboard } from "@/components/pages/dashboard-all-items/components/table/utils/types/types";
import { DashboardFolderItemNameEdit } from "@/components/pages/dashboard-all-items/components/table/components/cells/DashboardFolderItemNameEdit";
import { DashboardV2ItemMoveAction } from "@/components/app-navigation/components/page-components/dashboard/components/item-actions-menu/move/DashboardV2ItemMoveAction";
import { DashboardV2AddWidgetAction } from "@/components/app-navigation/components/page-components/dashboard/components/item-actions-menu/DashboardV2AddWidgetAction";
import { updateDashboardHierarchyDashboardThunk } from "@/store/dashboards/thunks/common/updateDashboardHierarchyDashboardThunk";
import { useAppDispatch } from "@/store/hooks";

interface DashboardNameCellProps {
  folderId?: string;
  data: PartialDashboard;
}

export const DashboardNameCell: FC<DashboardNameCellProps> = ({
  data,
  folderId,
}) => {
  const dispatch = useAppDispatch();

  const { id, name, protection_details, visibility } = data;
  const isProtected = !!protection_details?.is_protected;
  const navigate = useNavigate();

  const [copied, setCopied] = useState(false);

  const { can } = useAppAbility();
  const canEditDashboard = can("edit", "dashboard");

  const { ref, hovered } = useAGGridRowHoverHook();
  const [forceHover, setForceHover] = useState(false);
  const [editName, toggleEditName] = useToggle(false);

  const openHandler = () => setForceHover(true);
  const closeHandler = () => setForceHover(false);

  const clickHandler = useCallback(() => {
    navigate(`/dashboards/${id}`);
  }, [navigate, id]);

  const copyHandler = useCallback(() => {
    setCopied(true);

    setTimeout(() => setCopied(false), 3000);
  }, []);

  const setDashboardAsHomeHandler = useCallback(() => {
    if (!id) {
      return;
    }

    dispatch(
      updateDashboardHierarchyDashboardThunk({
        dashboardId: id,
        body: { is_home_page: true },
        updatedMessage: "Homepage successfully changed.",
      }),
    );
  }, [dispatch, id]);

  if (editName && id && name) {
    return (
      <DashboardFolderItemNameEdit
        id={id}
        name={name}
        onSubmit={toggleEditName}
      />
    );
  }

  return (
    <Stack
      ref={ref}
      direction="row"
      spacing={1}
      alignItems="center"
      onClick={clickHandler}
      sx={{ cursor: "pointer" }}
    >
      <GridViewOutlinedIcon fontSize="small" />

      {visibility === "visible_only_to_me" && (
        <LockOutlinedIcon color="action" sx={{ fontSize: 16 }} />
      )}

      {data.is_home_page && (
        <Tooltip arrow title="Homepage">
          <HomeOutlinedIcon fontSize="small" />
        </Tooltip>
      )}

      {isProtected && (
        <VerifiedUserOutlined color="action" sx={{ fontSize: 16 }} />
      )}

      <TypographyWithTooltip variant="inherit" title={name} />

      {(hovered || forceHover) && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          onClick={preventDefaultAndStopPropagation}
        >
          <Tooltip
            arrow
            placement="top"
            title={copied ? "Copied!" : "Copy Link"}
          >
            <span>
              <CopyToClipboard
                onCopy={copyHandler}
                text={`${window.location.host}/dashboards/${id}`}
              >
                <IconButton
                  size="small"
                  disabled={copied}
                  sx={iconButtonStyles}
                >
                  <LinkIcon fontSize="small" color="action" />
                </IconButton>
              </CopyToClipboard>
            </span>
          </Tooltip>

          {!isProtected && canEditDashboard && (
            <Fragment>
              <Tooltip arrow title="Edit Name" placement="top">
                <span>
                  <IconButton
                    size="small"
                    sx={iconButtonStyles}
                    onClick={toggleEditName}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              {id && name && visibility && (
                <DashboardV2ItemMoveAction
                  iconButton
                  id={id}
                  name={name}
                  folderId={folderId}
                  onOpen={openHandler}
                  onClose={closeHandler}
                  visibility={visibility}
                  isProtected={isProtected}
                />
              )}

              {id && <DashboardV2AddWidgetAction iconButon dashboardId={id} />}
            </Fragment>
          )}

          {canEditDashboard && !data.is_home_page && (
            <Tooltip arrow title="Set as Homepage" placement="top">
              <span>
                <IconButton size="small" onClick={setDashboardAsHomeHandler}>
                  <HomeOutlinedIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const iconButtonStyles = { p: 0.3, "&:hover": { bgcolor: "grey.200" } };
const preventDefaultAndStopPropagation = (e: MouseEvent<HTMLElement>) => {
  e.stopPropagation();
  e.preventDefault();
};
